import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Loader2, AlertCircle, CheckCircle, Calendar, Clock, Gift, PartyPopper,
  MapPin, CreditCard, Phone, ShieldCheck, ArrowRight,
} from "lucide-react";
import { BookingLocationPicker } from "../components/landing/BookingLocationPicker";
import PaymentTrustBadge from "../components/PaymentTrustBadge";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const WA_LINK = "https://wa.me/919930001848";

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr + (dateStr.includes("T") ? "" : "T00:00:00"));
    return d.toLocaleDateString("en-IN", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
  } catch { return dateStr; }
};

const SummaryRow = ({ icon: Icon, color, label, value }) => (
  <div className="flex items-start gap-3">
    <Icon size={18} className={`${color} mt-0.5 flex-shrink-0`} />
    <div className="min-w-0">
      <p className="text-[11px] text-[#94a3b8] uppercase tracking-wider font-medium">{label}</p>
      <p className="text-[#1E1B4B] font-semibold break-words">{value || "—"}</p>
    </div>
  </div>
);

const CompleteBooking = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [fatal, setFatal] = useState("");
  const [step, setStep] = useState("address"); // address | pay | done
  const [paying, setPaying] = useState(false);
  const [savingAddr, setSavingAddr] = useState(false);
  const [doneRef, setDoneRef] = useState("");

  // address form state
  const [address, setAddress] = useState("");
  const [pincode, setPincode] = useState("");
  const [lat, setLat] = useState(null);
  const [lng, setLng] = useState(null);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/booking-link/${token}`);
      const d = await res.json().catch(() => ({}));
      if (!res.ok) {
        setFatal(d?.detail || "This booking link is not available.");
        setData(null);
      } else {
        setData(d);
        setAddress(d.address || "");
        setPincode(d.pincode || "");
        setLat(d.location_lat ?? null);
        setLng(d.location_lng ?? null);
        if (d.payment_status === "paid" || d.status === "completed") {
          setDoneRef(d.booking_id || "");
          setStep("done");
        } else if (d.address_confirmed && d.address) {
          setStep("pay");
        } else {
          setStep("address");
        }
      }
    } catch {
      setFatal("Unable to load your booking. Please check your connection.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const handleLocationPicked = useCallback((p) => {
    if (!p) return;
    if (p.address) setAddress(p.address);
    if (p.pincode) setPincode(p.pincode);
    if (p.lat != null) setLat(p.lat);
    if (p.lng != null) setLng(p.lng);
  }, []);

  const addressValid = address.trim().length >= 8 && /^\d{6}$/.test(pincode.trim()) && lat != null && lng != null;

  const handleConfirmAddress = async () => {
    setError("");
    if (!addressValid) {
      setError("Please enter your full address, a valid 6-digit pincode, and drop a map pin.");
      return;
    }
    setSavingAddr(true);
    try {
      const res = await fetch(`${API_URL}/api/booking-link/${token}/address`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ address: address.trim(), pincode: pincode.trim(), location_lat: lat, location_lng: lng }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { setError(d?.detail || "Could not save address."); }
      else { setStep("pay"); window.scrollTo({ top: 0, behavior: "smooth" }); }
    } catch { setError("Network error. Please try again."); }
    setSavingAddr(false);
  };

  const handlePay = async () => {
    setError("");
    setPaying(true);
    try {
      const res = await fetch(`${API_URL}/api/booking-link/${token}/create-order`, { method: "POST" });
      const order = await res.json().catch(() => ({}));
      if (!res.ok) { setError(order?.detail || "Could not start payment."); setPaying(false); return; }

      const options = {
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: "DoozyGo®",
        description: `Advance - ${data?.package || "Party"} (${data?.occasion || ""})`,
        order_id: order.order_id,
        prefill: { name: order.customer_name, email: order.customer_email, contact: order.customer_phone },
        theme: { color: "#E83E8C" },
        handler: async (response) => {
          try {
            const vr = await fetch(`${API_URL}/api/booking-link/${token}/verify-payment`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              }),
            });
            const vd = await vr.json().catch(() => ({}));
            if (vr.ok) {
              setDoneRef(vd.booking_id || "");
              setStep("done");
              window.scrollTo({ top: 0, behavior: "smooth" });
            } else {
              setError(vd?.detail || "Payment could not be verified. If money was deducted, it will auto-confirm shortly.");
            }
          } catch {
            setError("Payment verification failed. If money was deducted it will confirm automatically — or reopen this link to retry.");
          }
          setPaying(false);
        },
        modal: { ondismiss: () => setPaying(false) },
      };

      if (window.Razorpay) { new window.Razorpay(options).open(); }
      else { setError("Payment gateway not loaded. Please refresh."); setPaying(false); }
    } catch (e) {
      setError(e.message || "Something went wrong.");
      setPaying(false);
    }
  };

  // ── Loading / fatal states ──
  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFFAF5] flex items-center justify-center" data-testid="complete-booking-loading">
        <Loader2 className="animate-spin text-[#E83E8C]" size={40} />
      </div>
    );
  }
  if (fatal) {
    return (
      <div className="min-h-screen bg-[#FFFAF5] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center" data-testid="complete-booking-error">
          <AlertCircle size={48} className="mx-auto text-red-400 mb-4" />
          <h2 className="text-xl font-bold text-[#1E1B4B] mb-2">Link Unavailable</h2>
          <p className="text-[#475569] mb-6">{fatal}</p>
          <a href={WA_LINK} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#25D366] text-white font-semibold text-sm">
            <Phone size={16} /> Message us on WhatsApp
          </a>
        </div>
      </div>
    );
  }

  const StepPill = ({ n, label, active, done }) => (
    <div className="flex items-center gap-2">
      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
        done ? "bg-emerald-500 text-white" : active ? "bg-[#E83E8C] text-white" : "bg-gray-200 text-gray-500"}`}>
        {done ? <CheckCircle size={14} /> : n}
      </span>
      <span className={`text-xs font-semibold ${active || done ? "text-[#1E1B4B]" : "text-gray-400"}`}>{label}</span>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FFFAF5]" data-testid="complete-booking-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#E83E8C] to-[#FF9F1C] py-6 px-4">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="https://customer-assets.emergentagent.com/job_party-in-2hrs/artifacts/47ayyz1k_SUMIT%20DOOZYGO%20LOGO%20-%20AUG%202023.png" alt="DoozyGo®" className="h-9" />
          </Link>
          <span className="text-white/90 text-sm font-semibold">Complete your booking</span>
        </div>
      </div>

      <div className="max-w-lg mx-auto p-4">
        {/* Steps */}
        {step !== "done" && (
          <div className="flex items-center justify-between bg-white rounded-xl shadow-sm px-4 py-3 mb-4" data-testid="step-indicator">
            <StepPill n={1} label="Address" active={step === "address"} done={step === "pay"} />
            <div className="flex-1 h-px bg-gray-200 mx-2" />
            <StepPill n={2} label="Payment" active={step === "pay"} done={false} />
          </div>
        )}

        {/* Booking summary */}
        <div className="bg-white rounded-2xl shadow-md p-5 mb-4" data-testid="booking-summary-card">
          <h1 className="text-lg font-bold text-[#1E1B4B] mb-4">Your party details</h1>
          <div className="grid grid-cols-1 gap-4">
            <SummaryRow icon={PartyPopper} color="text-[#FF9F1C]" label="Package" value={data?.package} />
            <SummaryRow icon={Gift} color="text-purple-500" label="Occasion" value={data?.occasion} />
            <SummaryRow icon={Calendar} color="text-[#E83E8C]" label="Event date" value={formatDate(data?.date)} />
            <SummaryRow icon={Clock} color="text-teal-500" label="Setup time" value={data?.setup_time} />
          </div>
          <div className="mt-4 pt-4 border-t border-dashed border-gray-200 flex items-center justify-between">
            <span className="text-[#475569] text-sm font-medium">Advance to pay now</span>
            <span className="text-xl font-bold text-[#E83E8C]" data-testid="advance-amount">₹{(data?.amount_rs || 0).toLocaleString("en-IN")}</span>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4 flex items-start gap-2" data-testid="complete-booking-inline-error">
            <AlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={18} />
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        {/* STEP 1 — Address */}
        {step === "address" && (
          <div className="bg-white rounded-2xl shadow-md p-5 mb-4" data-testid="address-step">
            <h2 className="text-base font-bold text-[#1E1B4B] mb-1 flex items-center gap-2"><MapPin size={18} className="text-teal-500" /> Service address</h2>
            <p className="text-xs text-[#94a3b8] mb-4">Drop a pin on the map, then check the address below. This is where our team will set up.</p>

            <BookingLocationPicker onLocationPicked={handleLocationPicked} />

            <div className="mt-4 space-y-3">
              <div>
                <label className="text-xs font-semibold text-[#475569] mb-1 block">Full address</label>
                <textarea
                  value={address}
                  onChange={(e) => setAddress(e.target.value.slice(0, 300))}
                  rows={3}
                  placeholder="Flat / House no, building, street, area, city"
                  className="w-full px-3 py-2.5 rounded-xl border border-gray-200 focus:border-[#E83E8C] focus:ring-2 focus:ring-[#E83E8C]/20 outline-none text-sm resize-none"
                  data-testid="address-input"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#475569] mb-1 block">Pincode</label>
                <input
                  value={pincode}
                  onChange={(e) => setPincode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  inputMode="numeric"
                  placeholder="6-digit pincode"
                  className="w-full px-3 py-2.5 rounded-xl border border-gray-200 focus:border-[#E83E8C] focus:ring-2 focus:ring-[#E83E8C]/20 outline-none text-sm"
                  data-testid="pincode-input"
                />
              </div>
            </div>

            <button
              onClick={handleConfirmAddress}
              disabled={savingAddr || !addressValid}
              className="mt-5 w-full py-3.5 rounded-xl font-bold text-white text-base bg-gradient-to-r from-[#E83E8C] to-[#FF9F1C] hover:shadow-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="confirm-address-btn"
            >
              {savingAddr ? <Loader2 className="animate-spin" size={18} /> : <>Confirm address <ArrowRight size={18} /></>}
            </button>
          </div>
        )}

        {/* STEP 2 — Payment */}
        {step === "pay" && (
          <div className="bg-white rounded-2xl shadow-md p-5 mb-4" data-testid="payment-step">
            <h2 className="text-base font-bold text-[#1E1B4B] mb-3 flex items-center gap-2"><CreditCard size={18} className="text-[#E83E8C]" /> Payment</h2>
            <div className="bg-[#FFFAF5] rounded-xl p-4 mb-4">
              <div className="flex items-start gap-2 mb-2">
                <MapPin size={15} className="text-teal-500 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-[#475569]" data-testid="confirmed-address">{address}, {pincode}</p>
              </div>
              <button onClick={() => setStep("address")} className="text-xs font-semibold text-[#E83E8C] hover:underline ml-6" data-testid="edit-address-btn">Edit address</button>
            </div>
            <div className="flex items-center justify-between mb-4">
              <span className="text-[#1E1B4B] font-bold">Advance payable</span>
              <span className="text-xl font-bold text-[#E83E8C]">₹{(data?.amount_rs || 0).toLocaleString("en-IN")}</span>
            </div>
            <PaymentTrustBadge variant="compact" />
            <button
              onClick={handlePay}
              disabled={paying}
              className="mt-3 w-full py-3.5 rounded-xl font-bold text-white text-base bg-gradient-to-r from-[#E83E8C] to-[#FF9F1C] hover:shadow-lg transition-all disabled:opacity-60 flex items-center justify-center gap-2"
              data-testid="proceed-payment-btn"
            >
              {paying ? <><Loader2 className="animate-spin" size={18} /> Processing…</> : <><CreditCard size={18} /> Proceed to Payment</>}
            </button>
            <p className="text-[11px] text-[#94a3b8] text-center mt-3 flex items-center justify-center gap-1">
              <ShieldCheck size={12} /> Secure payment via Razorpay. You can reopen this link to retry anytime.
            </p>
          </div>
        )}

        {/* STEP 3 — Done */}
        {step === "done" && (
          <div className="bg-white rounded-2xl shadow-md p-6 mb-4 text-center" data-testid="confirmation-step">
            <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-4">
              <CheckCircle size={34} className="text-emerald-600" />
            </div>
            <h2 className="text-xl font-bold text-[#1E1B4B] mb-1">Booking Confirmed!</h2>
            <p className="text-[#475569] text-sm mb-4">Your advance is paid and your party is locked in. We've sent a confirmation to your WhatsApp.</p>
            {doneRef && (
              <div className="inline-block bg-[#FFFAF5] rounded-xl px-4 py-2 mb-4">
                <p className="text-[11px] text-[#94a3b8] uppercase tracking-wider">Booking reference</p>
                <p className="text-[#1E1B4B] font-bold font-mono" data-testid="confirmation-ref">{doneRef}</p>
              </div>
            )}
            <div className="text-left bg-[#FFFAF5] rounded-xl p-4 space-y-3 mb-5">
              <SummaryRow icon={PartyPopper} color="text-[#FF9F1C]" label="Package" value={data?.package} />
              <SummaryRow icon={Gift} color="text-purple-500" label="Occasion" value={data?.occasion} />
              <SummaryRow icon={Calendar} color="text-[#E83E8C]" label="Event date" value={formatDate(data?.date)} />
              <SummaryRow icon={Clock} color="text-teal-500" label="Setup time" value={data?.setup_time} />
              <SummaryRow icon={MapPin} color="text-teal-500" label="Address" value={`${address}${pincode ? ", " + pincode : ""}`} />
              <SummaryRow icon={CheckCircle} color="text-emerald-500" label="Payment" value={`Advance ₹${(data?.amount_rs || 0).toLocaleString("en-IN")} — Paid`} />
            </div>
            <a href={WA_LINK} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#25D366] text-white font-semibold text-sm" data-testid="confirmation-whatsapp-btn">
              <Phone size={16} /> Chat with us on WhatsApp
            </a>
          </div>
        )}

        {/* Contact fallback */}
        {step !== "done" && (
          <div className="text-center pb-8">
            <p className="text-[#94a3b8] text-xs mb-2">Need help with your booking?</p>
            <a href={WA_LINK} className="inline-flex items-center gap-1.5 text-[#E83E8C] text-sm font-semibold hover:underline">
              <Phone size={14} /> Message us on WhatsApp
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompleteBooking;
